import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { fetchPrintBazaarDocs } from '../../lib/supabase/api/fetch-print-bazaar-docs';
import { deletePrintBazaarDoc } from '../../lib/supabase/api/delete-print-bazaar-doc';
import { UploadDialog } from './UploadDialog';
import { toast } from 'react-hot-toast';

// This type represents the document data structure for the PrintBazaar documents
interface UploadedDocument {
  id: string;
  user_id: string;
  title: string;
  description: string | null;
  file_name: string;
  file_path: string;
  cover_image_path: string | null;
  file_size: number;
  file_type: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  updated_at: string;
  views: number;
  downloads: number;
  cost: number;
  earnings: number;
}

const MyUploads: React.FC = () => {
  const [documents, setDocuments] = useState<UploadedDocument[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  // Fetch user's documents when component mounts
  useEffect(() => {
    fetchUserDocuments();
  }, []);

  // Function to fetch the user's documents from PrintBazaar
  const fetchUserDocuments = async () => {
    setIsLoading(true);
    try {
      const docs = await fetchPrintBazaarDocs(true); // true = only fetch my documents
      setDocuments(docs || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to delete document
  const deleteDocument = async (id: string) => {
    // Dismiss any existing toast first to avoid stacking
    toast.dismiss();
    
    toast((t) => (
      <div className="flex flex-col gap-2">
        <span>Are you sure you want to delete this document?</span>
        <div className="flex gap-2 justify-end">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => toast.dismiss(t.id)}
          >
            Cancel
          </Button>
          <Button 
            variant="destructive" 
            size="sm"
            onClick={async () => {
              toast.dismiss(t.id);
              try {
                console.log('Deleting document:', id);
                const result = await deletePrintBazaarDoc(id);
                
                if (result.success) {
                  // Update state to remove the deleted document
                  setDocuments(documents.filter(doc => doc.id !== id));
                  // Show success toast notification after dismissing current toast
                  setTimeout(() => {
                    toast.success('Document deleted successfully', { position: "top-center" });
                  }, 100);
                } else {
                  throw new Error(result.message);
                }
              } catch (error) {
                console.error('Error deleting document:', error);
                setTimeout(() => {
                  toast.error('Failed to delete document. Please try again.', { position: "top-center" });
                }, 100);
              }
            }}
          >
            Delete
          </Button>
        </div>
      </div>
    ), { 
      duration: 5000,
      position: "top-center",
      id: "delete-confirmation" // Use a fixed ID to prevent multiple confirmation toasts
    });
  };

  // Helper function to format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' bytes';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / 1048576).toFixed(1) + ' MB';
  };

  // Helper function to get status badge color
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-500 hover:bg-green-600">Approved</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500 hover:bg-yellow-600">Pending</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500 hover:bg-red-600">Rejected</Badge>;
      default:
        return <Badge>Unknown</Badge>;
    }
  };

  // Helper function to format date
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString();
  };

  // Calculate stats
  const stats = {
    total_docs: documents.length,
    total_earnings: documents.reduce((sum, doc) => sum + (doc.earnings || 0), 0),
    total_downloads: documents.reduce((sum, doc) => sum + (doc.downloads || 0), 0),
    total_views: documents.reduce((sum, doc) => sum + (doc.views || 0), 0)
  };

  return (
    <div>
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-sm font-medium text-gray-500">Total Documents</h3>
          <p className="text-2xl font-bold text-gray-900">{stats.total_docs}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-sm font-medium text-gray-500">Total Earnings</h3>
          <p className="text-2xl font-bold text-green-600">₹{stats.total_earnings.toFixed(2)}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-sm font-medium text-gray-500">Total Downloads</h3>
          <p className="text-2xl font-bold text-blue-600">{stats.total_downloads}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-sm font-medium text-gray-500">Total Views</h3>
          <p className="text-2xl font-bold text-purple-600">{stats.total_views}</p>
        </div>
      </div>

      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-800">My PrintBazaar Documents</h2>
        <Button 
          onClick={() => setIsDialogOpen(true)}
          className="bg-blue-600 hover:bg-blue-700"
        >
          Upload New Document
        </Button>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
          <p className="mt-2 text-gray-600">Loading documents...</p>
        </div>
      ) : documents.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-500 text-lg">You haven't uploaded any documents yet.</p>
          <Button 
            onClick={() => setIsDialogOpen(true)}
            className="mt-4 bg-blue-600 hover:bg-blue-700"
          >
            Upload Your First Document
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {documents.map((doc) => (
            <div key={doc.id} className="bg-white border rounded-lg overflow-hidden hover:shadow-md transition-all duration-200">
              <div className="flex flex-col sm:flex-row">
                {/* Image Section */}
                <div className="p-4 sm:p-0 flex justify-center sm:block">
                  <div className="w-[120px] sm:w-[170px] aspect-[1/1.414] bg-gray-100 flex-shrink-0 flex items-center justify-center rounded sm:rounded-none">
                    {doc.cover_image_path ? (
                      <img 
                        src={`${supabaseUrl}/storage/v1/object/public/print-bazaar-docs/${doc.cover_image_path}`}
                        alt={doc.title} 
                        className="w-full h-full object-contain rounded sm:rounded-none"
                      />
                    ) : (
                      <svg className="w-12 h-12 text-gray-300" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                </div>

                {/* Content Section */}
                <div className="flex-grow p-4">
                  <div className="flex flex-col h-full">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">{doc.title}</h3>
                        {doc.description && (
                          <p className="text-sm text-gray-600 mb-3 line-clamp-2">{doc.description}</p>
                        )}
                      </div>
                      {getStatusBadge(doc.status)}
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-2 text-sm">
                      <div className="space-y-1">
                        <p className="text-gray-500">File Details</p>
                        <p className="font-medium">{doc.file_name}</p>
                        <p className="text-gray-600">{formatFileSize(doc.file_size)}</p>
                        <p className="font-medium">{doc.cost === 0 ? 'FREE' : `₹${doc.cost.toFixed(2)}`}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-gray-500">Statistics</p>
                        <p className="font-medium">{doc.views} Views</p>
                        <p className="font-medium">{doc.downloads} Downloads</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-gray-500">Earnings</p>
                        <p className="font-bold text-green-600 text-lg">₹{doc.earnings?.toFixed(2) || '0.00'}</p>
                      </div>
                    </div>

                    <div className="flex gap-3 mt-4 pt-4 border-t">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => window.open(`${supabaseUrl}/storage/v1/object/public/print-bazaar-docs/${doc.file_path}`, '_blank')}
                        className="flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        Preview
                      </Button>
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={() => deleteDocument(doc.id)}
                        className="flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <UploadDialog 
        open={isDialogOpen} 
        onOpenChange={setIsDialogOpen}
      />
    </div>
  );
};

export default MyUploads;
